import React, {useEffect, useState} from 'react'
import {
    Box,
    Button,
    Link,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'

/* Components */
import ReviewsHeadline from './partials/reviews-headline'
import ReviewHeading from './partials/reviews-heading'
import ReviewCard from './partials/review-card'
import LoadMore from './partials/load-more'
import ExpandedImage from './partials/expanded-image'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

/* API HOOKS */
import {useBazarVoice, useReviewForm} from '../../integrations/bazarvoice/hooks/use-bazarvoice'

import {sendLoadReviewEvent} from '../../utils/gtm-utils'
import {useInView} from '../../hooks/use-in-view'
import {useHeaderContext} from '../../contexts/header-context'

/* Constants */
const MAX_REVIEWS_SLIDES_ON_MOBILE = 5

const PDPReviews = ({product}) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const pid = product?.id;
    const {isLoading, reviews: initialReviews, reviewsError} = useBazarVoice({pid});
    const {getReviewForm, isLoading: isReviewFormLoading} = useReviewForm({pid})

    const [reviewsData, setReviewsData] = useState(initialReviews);
    const [selectedReview, setSelectedReview] = useState({});
    const [reviewFormData, setReviewFormData] = useState({});
    const [noReviews, setNoReviews] = useState(false);

    const {headerHeight} = useHeaderContext()
    const {ref: bvHeaderRef, isInView} = useInView({
        rootMargin: `${headerHeight}px`
    })

    if (reviewsError) {
        console.error('[PDPReviews]:', reviewsError)
    }

    const fetchReviewForm = async () => {
        try {
            const result = await getReviewForm({pid});

            if (!result.isError) {
                setReviewFormData(result?.data?.Data?.Fields);
            } else {
                console.warn('Review form returned error:', result);
            }
        } catch (error) {
            console.error('Error fetching review form data:', error);
        }
    };

    useEffect(() => {
        if (pid && reviewFormData && Object.keys(reviewFormData).length === 0) {
            fetchReviewForm();
        }
    }, [pid]);

    useEffect(() => {
        if (isInView && initialReviews) {
            // GTM Modification
            sendLoadReviewEvent(product, initialReviews)
            setReviewsData({
                options: {
                    pid: pid,
                    page: 0,
                    sort: 'SubmissionTime:desc'
                },
                reviews: initialReviews
            })
            setNoReviews(initialReviews?.Results?.length === 0)
        }
    }, [initialReviews, isInView])

    const closeAllReviewsModal = () => {
        onClose()
    }

    return (
        <Box maxWidth="926px" margin="0 auto" pb={{base: 0, lg: 20}} ref={bvHeaderRef}>
            <ReviewsHeadline product={product} noReviews={noReviews} onOpen={onOpen} isLoading={isReviewFormLoading} reviewFormData={reviewFormData} />
            <Skeleton isLoaded={!isLoading}>
                <HideOnDesktop mb={{base: 4}} px={{base: 4, lg: 6}}>
                    {/* CSS scroll container for mobile reviews */}
                    <Box
                        display="flex"
                        gap="2px"
                        overflowX="auto"
                        mx={{base: '-16px', lg: '-24px'}}
                        pl={{base: '16px', lg: '24px'}}
                        pr={{base: '16px', lg: '24px'}}
                        sx={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            },
                            WebkitOverflowScrolling: 'touch',
                            '& > *': {
                                flexShrink: 0,
                                width: '255px'
                            }
                        }}
                    >
                        {reviewsData?.reviews?.Results?.slice(0, MAX_REVIEWS_SLIDES_ON_MOBILE)?.map(
                            (review) => (
                                <Box key={'review-slide-' + review.Id} height="auto">
                                    <ReviewCard
                                        key={'rc-mobile-' + review.Id}
                                        data-testid={`review-card-mobile-${review.Id}`}
                                        review={review}
                                        setSelectedReview={setSelectedReview}
                                        configs={{
                                            styles: {
                                                container: {
                                                    px: 2,
                                                    py: 4,
                                                    border: '1px solid',
                                                    borderColor: 'gray.50',
                                                    h: '100%'
                                                }
                                            },
                                            enableReadMore: true,
                                            showImagesOnTopOfReviewText: true,
                                            showFooterActions: false
                                        }}
                                    />
                                </Box>
                            )
                        )}
                    </Box>
                </HideOnDesktop>
                <HideOnMobile>
                    <ReviewHeading
                        product={product}
                        reviews={reviewsData}
                        setReviewsData={setReviewsData}
                    ></ReviewHeading>
                    {reviewsData?.reviews?.Results?.map((review) => (
                        <ReviewCard
                            key={'rc-desktop-' + review.Id}
                            review={review}
                            setSelectedReview={setSelectedReview}
                            data-testid={`review-card-desktop-${review.Id}`}
                        />
                    ))}
                    <Box textAlign={{base: 'center'}}>
                        <LoadMore
                            pid={product?.id}
                            product={product}
                            reviews={reviewsData}
                            setReviewsData={setReviewsData}
                        />
                    </Box>
                </HideOnMobile>

                {/* Read all reviews and modal */}
                {!noReviews && (
                    <HideOnDesktop>
                        <Button
                            as={Link}
                            href='#'
                            variant={'link'}
                            aria-label='Open read all review modal'
                            fontSize={{base: 'sm'}}
                            lineHeight={{base: '100%'}}
                            textTransform={{base: 'uppercase'}}
                            fontWeight={700}
                            onClick={(e) => {
                                if(e) e.preventDefault()
                                onOpen()
                            }}
                            mb={{base: '62px'}}
                            px={{base: 4}}
                        >
                            <FormattedMessage
                                defaultMessage="Read all reviews"
                                id="pdp_reviews.text.read.more"
                            />
                        </Button>

                        <Drawer
                            placement="right"
                            isOpen={isOpen}
                            onOpen={onOpen}
                            onClose={onClose}
                            size={{base: 'full', md: 'sm'}}
                            motionPreset="slideInBottom"
                            scrollBehavior="inside"
                            isFullHeight={true}
                        >
                            <DrawerOverlay />
                            <DrawerContent marginTop={0}>
                                <DrawerBody
                                    padding="30px 16px"
                                    sx={{
                                        scrollbarGutter: {base: 'auto', lg: 'stable'}
                                    }}
                                    position="relative"
                                >
                                    <DrawerCloseButton
                                        w={{base: 6}}
                                        h={{base: 6}}
                                        top={{base: '28px'}}
                                        right={{base: '16px'}}
                                    />
                                    <Box id="reviews-modal-toast-container"></Box>
                                    <ReviewsHeadline
                                        noReviews={noReviews}
                                        product={product}
                                        isLoading={isReviewFormLoading}
                                        reviewFormData={reviewFormData}
                                        configs={{
                                            styles: {
                                                container: {
                                                    backgroundColor: 'white',
                                                    mb: 0,
                                                    px: 0,
                                                    pt: 0
                                                },
                                                writeReviewCTA: {
                                                    variant: 'solid',
                                                    backgroundColor: 'black'
                                                }
                                            },
                                            showTotalReviews: false
                                        }}
                                    />
                                    <ReviewHeading
                                        product={product}
                                        reviews={reviewsData}
                                        setReviewsData={setReviewsData}
                                    ></ReviewHeading>
                                    {reviewsData?.reviews?.Results?.map((review) => (
                                        <ReviewCard
                                            toastContainerId='reviews-modal-toast-container'
                                            key={'rc-modal-' + review.Id}
                                            review={review}
                                            setSelectedReview={setSelectedReview}
                                        />
                                    ))}
                                    <Box textAlign={{base: 'center'}}>
                                        <LoadMore
                                            pid={product?.id}
                                            reviews={reviewsData}
                                            setReviewsData={setReviewsData}
                                        />
                                    </Box>
                                </DrawerBody>
                            </DrawerContent>
                        </Drawer>
                    </HideOnDesktop>
                )}

                {/* Expanded image modal */}
                <ExpandedImage
                    selectedReview={selectedReview}
                    setSelectedReview={setSelectedReview}
                    closeOtherModals={{closeAllReviewsModal}}
                />
            </Skeleton>
        </Box>
    )
}

PDPReviews.propTypes = {
    product: PropTypes.object
}

export default PDPReviews
